import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { Tooltip } from 'Shared/components';
import styles from './DescriptionCell.module.css';

type DescriptionCellProps = {
  description: string | null | undefined;
  isInactive?: boolean;
};

// Re-attaches the observer when the ref's element changes (e.g. when we
// re-render the span inside the Tooltip wrapper). useRef would silently
// keep observing the detached span; a callback ref runs on every swap.
const useOverflowDetection = () => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: HTMLSpanElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;

    const measure = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    measure();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(el);
  }, []);

  return { ref, isOverflowing };
};

export const DescriptionCell: React.FC<DescriptionCellProps> = ({
  description,
  isInactive,
}) => {
  const trimmed = description?.trim();
  const { ref, isOverflowing } = useOverflowDetection();

  if (!trimmed) {
    return <span className={isInactive ? styles.dimmed : undefined}>—</span>;
  }

  const text = (
    <span
      ref={ref}
      // title is a keyboard- and screen-reader-accessible fallback for the
      // mouse-only styled Tooltip; only set when content is truncated.
      title={isOverflowing ? trimmed : undefined}
      className={classnames(styles.trigger, { [styles.dimmed]: isInactive })}
    >
      {trimmed}
    </span>
  );

  if (!isOverflowing) {
    return <div className={styles.cell}>{text}</div>;
  }

  const tooltipContent = (
    <div className={styles.card}>
      <div className={styles.header}>DESCRIPTION</div>
      <div className={styles.body}>{trimmed}</div>
    </div>
  );

  return (
    <div className={styles.cell}>
      <Tooltip
        tooltip={tooltipContent}
        position="bottom"
        popoverClassName={styles.popoverReset}
      >
        {text}
      </Tooltip>
    </div>
  );
};
